<script setup lang="ts">
/**
 * BO Letters — Appendix A/B/C (M32109) — 설문 → 선택 문서만 플랫 표시 (Stage 없음).
 */
import { ref, computed, watch } from 'vue';
import type { Component } from 'vue';
import AppendixADoc from './AppendixADoc.vue';
import AppendixBCorporateDoc from './AppendixBCorporateDoc.vue';
import AppendixBIndividualDoc from './AppendixBIndividualDoc.vue';
import AppendixCCorporateDoc from './AppendixCCorporateDoc.vue';
import AppendixCIndividualDoc from './AppendixCIndividualDoc.vue';
import BoAppendixSurveyPanel from './BoAppendixSurvey.vue';
import {
  normalizeAppendixSurvey,
  resolveIncludedAppendixDocs,
  summarizeAppendixSurvey,
  type BoAppendixDocKey,
  type BoAppendixSurvey,
} from './boAppendixSurvey';

interface DocItem { key: BoAppendixDocKey; icon: string; name: string; comp: Component }

const ALL_DOCS: DocItem[] = [
  { key: 'appendix_a', icon: '📝', name: 'Appendix A — Particulars of Beneficial Owner', comp: AppendixADoc },
  { key: 'appendix_b_corporate', icon: '🏢', name: 'Appendix B — Others as BO (Corporate)', comp: AppendixBCorporateDoc },
  { key: 'appendix_b_individual', icon: '🙍', name: 'Appendix B — Others as BO (Individual)', comp: AppendixBIndividualDoc },
  { key: 'appendix_c_corporate', icon: '👔', name: 'Appendix C — Senior Management (Corporate)', comp: AppendixCCorporateDoc },
  { key: 'appendix_c_individual', icon: '👤', name: 'Appendix C — Senior Management (Individual)', comp: AppendixCIndividualDoc },
];

const props = withDefaults(defineProps<{
  studio?: boolean;
  editMode?: boolean;
  creating?: boolean;
  survey?: BoAppendixSurvey;
}>(), {
  studio: false,
  editMode: false,
  creating: false,
});

const emit = defineEmits<{
  (e: 'create-task'): void;
  (e: 'update:survey', v: BoAppendixSurvey): void;
}>();

const localSurvey = ref<BoAppendixSurvey>(normalizeAppendixSurvey(props.survey));

watch(
  () => props.survey,
  (v) => {
    localSurvey.value = normalizeAppendixSurvey(v);
    if (v?.completed) localSurvey.value.completed = true;
  },
  { deep: true },
);

function onSurveyUpdate(v: BoAppendixSurvey) {
  localSurvey.value = normalizeAppendixSurvey(v);
  localSurvey.value.completed = !!v.completed;
  emit('update:survey', { ...localSurvey.value });
}

const included = computed(() => new Set(resolveIncludedAppendixDocs(localSurvey.value)));
const showSurvey = computed(() => !localSurvey.value.completed);
const docs = computed(() => ALL_DOCS.filter((d) => included.value.has(d.key)));
const totalDocs = computed(() => docs.value.length);
const createLabel = computed(() => (props.editMode ? 'Update Task' : 'Create Task'));
const surveySummary = computed(() => summarizeAppendixSurvey(localSurvey.value));

function reopenSurvey() {
  const next = normalizeAppendixSurvey({ ...localSurvey.value, completed: false });
  next.completed = false;
  localSurvey.value = next;
  emit('update:survey', next);
}
</script>

<template>
  <div class="allot-pkg">
    <BoAppendixSurveyPanel
      v-if="showSurvey"
      :model-value="localSurvey"
      @update:model-value="onSurveyUpdate"
      @complete="onSurveyUpdate"
    />

    <template v-else>
      <div class="sv-bar">
        <div class="sv-bar-txt">
          <b>Selected appendices</b>
          <small>{{ surveySummary }}</small>
        </div>
        <button type="button" class="sv-bar-btn" @click="reopenSurvey">Change selection</button>
      </div>

      <div v-for="(d, i) in docs" :key="d.key" class="pkg-doc">
        <div class="pkg-doclabel">
          <span class="ic">{{ d.icon }}</span>
          <span class="nm">{{ d.name }}</span>
          <span class="no">Doc {{ i + 1 }} / {{ totalDocs }}</span>
        </div>
        <div class="pkg-paper"><component :is="d.comp" /></div>
      </div>

      <div class="stage-nav">
        <span class="nav-count">{{ totalDocs }} form{{ totalDocs === 1 ? '' : 's' }}</span>
        <button
          v-if="!studio"
          class="nav-btn primary"
          :disabled="creating || !totalDocs"
          @click="emit('create-task')"
        >{{ creating ? (editMode ? 'Updating…' : 'Creating…') : createLabel }}</button>
      </div>
    </template>
  </div>
</template>

<style src="./boAppendixDoc.css"></style>

<style scoped>
.allot-pkg { color: #111; }
.sv-bar {
  display: flex; align-items: center; gap: 12px;
  width: 100%; max-width: 900px; margin: 0 auto 14px;
  padding: 10px 12px; background: #f4f7fc; border: 1px solid #e0e8f4; border-radius: 12px;
}
.sv-bar-txt { display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1; }
.sv-bar-txt b { font-size: 12px; color: #1f2d4d; }
.sv-bar-txt small { font-size: 11px; color: #6b778c; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sv-bar-btn {
  flex: none; border: 1px solid #d3dceb; background: #fff; color: #42506a;
  font-size: 12px; font-weight: 800; border-radius: 8px; padding: 7px 11px; cursor: pointer;
}
.sv-bar-btn:hover { background: #eef3fb; }
.pkg-doc { width: 100%; max-width: 900px; margin: 0 auto; }
.pkg-doc + .pkg-doc { margin-top: 30px; }
.pkg-doclabel { display: flex; align-items: center; gap: 7px; padding: 0 4px 9px; }
.pkg-doclabel .ic { font-size: 14px; line-height: 1; }
.pkg-doclabel .nm { font-size: 12px; font-weight: 800; color: #42506a; letter-spacing: .01em; }
.pkg-doclabel .no { margin-left: auto; font-size: 10.5px; font-weight: 800; color: #9aa6bd; white-space: nowrap; }
.pkg-paper {
  background: #fff; padding: 34px 38px;
  box-shadow: 0 4px 24px rgba(20, 30, 60, .12);
  font-family: Arial, 'Malgun Gothic', sans-serif; color: #111; font-size: 12px; line-height: 1.4;
  border-radius: 2px;
}
.stage-nav { display: flex; align-items: center; justify-content: center; gap: 14px; width: 100%; max-width: 900px; margin: 26px auto 6px; }
.nav-btn { border: 1px solid #d3dceb; background: #fff; color: #42506a; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 10px 18px; cursor: pointer; }
.nav-btn.primary { background: #1b64da; border-color: #1b64da; color: #fff; }
.nav-btn:disabled { opacity: .55; cursor: default; }
.nav-count { font-size: 12px; font-weight: 800; color: #8a97b5; }
</style>
